/**
 * Responsibility: read and change the named sub-collections of cards.
 *
 * Every mutation invalidates the collection as well as the tag list, because
 * the two are the same fact seen from two sides: the tag knows how many cards
 * wear it, and each card knows which tags it wears. Refreshing only one leaves
 * the page showing a count that disagrees with the cards under it - which
 * looks like a bug in the count rather than a stale cache.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { hubApi } from '../client';
import type { AddHolding, NewTag, TagEdit } from '../client';
import { marketKeys } from './useMarket';

export const tagKeys = {
  all: () => ['collection', 'tags'] as const,
};

export function useTags() {
  return useQuery({
    queryKey: tagKeys.all(),
    queryFn: () => hubApi.tags(),
  });
}

/** Invalidate both sides of the relationship. */
function useTagMutation<Input, Output>(run: (input: Input) => Promise<Output>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: tagKeys.all() }),
        client.invalidateQueries({ queryKey: marketKeys.collection() }),
        // The saved list is where a like's tags live, so it is as stale
        // as the other two after any of these.
        client.invalidateQueries({ queryKey: ['saved'] }),
      ]);
    },
  });
}

export function useAddTag() {
  return useTagMutation((body: NewTag) => hubApi.addTag(body));
}

export function useEditTag() {
  return useTagMutation(({ tagId, changes }: { tagId: string; changes: TagEdit }) =>
    hubApi.editTag(tagId, changes),
  );
}

export function useRemoveTag() {
  return useTagMutation((tagId: string) => hubApi.removeTag(tagId));
}

/**
 * Put a kept card in exactly these sub-collections.
 *
 * The same replace-not-merge call a holding uses, against the same ids, so a
 * grouping spans what is owned and what is only wanted.
 */
export function useSetLikeTags() {
  return useTagMutation(({ productKey, tags }: { productKey: string; tags: string[] }) =>
    hubApi.setLikeTags(productKey, tags),
  );
}

/**
 * Put a holding in exactly these sub-collections.
 *
 * The full set every time rather than an add and a remove: taking a card out
 * of a grouping is then the same call as putting it in, and there is no second
 * path that could disagree about the result.
 */
export function useSetHoldingTags() {
  return useTagMutation(({ itemId, tags }: { itemId: string; tags: string[] }) =>
    hubApi.setHoldingTags(itemId, tags),
  );
}

/**
 * Add a holding and put it straight into sub-collections.
 *
 * **Two writes, one action, and the order matters.** A grouping attaches to
 * a holding by its id, and the holding has no id until the server has made
 * it - so the tags cannot be sent with the add and cannot be sent before
 * it. Chaining them here keeps that ordering in one place instead of
 * repeating it on the four pages that add cards.
 *
 * The holding is returned either way. If the tag write fails the card is
 * still in the collection, which is the right half to keep: a card in no
 * grouping is findable, a grouping pointing at nothing is not.
 */
export function useAddHoldingWithTags() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ body, tagIds }: { body: AddHolding; tagIds: string[] }) => {
      const held = await hubApi.addToCollection(body);
      if (tagIds.length > 0) {
        await hubApi.setHoldingTags(held.id, tagIds);
      }
      return held;
    },
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: marketKeys.collection() }),
        client.invalidateQueries({ queryKey: tagKeys.all() }),
      ]);
    },
  });
}
